import {DeleteFilled, PlusOutlined, ReloadOutlined} from "@ant-design/icons";
import {Breadcrumb, Button, message, Modal, Table} from "antd";
import {ColumnsType} from "antd/es/table";
import React, {useContext, useEffect, useState} from "react";
import {Link, useNavigate} from "react-router-dom";
import {AppContextContext, SbomProjectGroupServiceContext} from "../../Contexts";
import Organization from "../../domain/Organization";
import SbomProjectGroup from "../../domain/SbomProjectGroup";
import {Permission} from "../../domain/auth/Permission";
import PagedResult from "../../service/PagedResult";
import {useTableHandler} from "../../sal-ui/TableHandler";
import * as globalStyles from "../App.module.css";
import MoreLess from "../common/MoreLess";
import {DocumentTitle} from "../DocumentTitle";
import * as styles from "./SbomProjectGroupList.module.css";
import {ascendingFirstSortDirections} from "../../sal-ui/TableSortDirections";

function SbomProjectGroupList() {
    const appContext = useContext(AppContextContext);
    const sbomProjectGroupService = useContext(SbomProjectGroupServiceContext);
    const navigate = useNavigate();
    const tableHandler = useTableHandler("name", {reloadFunction: reload});
    const [groups, setGroups] = useState<PagedResult<SbomProjectGroup>>();

    const hasManageOrg = appContext.user.hasPermission(Permission.SBOM_PROJECT__MANAGE_ORG);
    const userIsSystemAdmin = appContext.user.isSystemAdmin();

    const columns: ColumnsType<SbomProjectGroup> = [
        {
            dataIndex: "name",
            title: "Name",
            sorter: true,
            sortDirections: ascendingFirstSortDirections,
            defaultSortOrder: "ascend",
            render: renderName
        },
        {
            dataIndex: "description",
            title: "Description",
            render: value => value || <i>no description</i>
        },
        {
            dataIndex: "projects",
            title: "Projects",
            render: renderProjects
        }
    ];

    if (userIsSystemAdmin) {
        columns.push({
            dataIndex: "organization",
            title: "Organization",
            render: renderOrganization
        });
    }

    if (hasManageOrg) {
        columns.push({
            title: "Actions",
            width: 100,
            align: "center",
            className: globalStyles["table-actions"],
            render: renderAction
        });
    }

    useEffect(() => {
        tableHandler.reload();
    }, []);

    const title = "Project groups";

    return (
        <DocumentTitle title={title}>
            <>
                <Breadcrumb className={globalStyles["common__breadcrumb"]}
                            items={[
                                {title: appContext.config?.appName},
                                {title}
                            ]}
                />

                <h1>{title}</h1>

                <div className={globalStyles["common__top-button-bar"]}>
                    <Button className={"btn-seamless"} icon={<ReloadOutlined/>} onClick={tableHandler.reload}/>

                    {hasManageOrg &&
                        <Button type={"primary"}
                                icon={<PlusOutlined/>}
                                onClick={() => navigate("/sbom-project-groups/add")}>Add</Button>
                    }
                </div>

                <Table className={styles.table}
                       showSorterTooltip={false}
                       loading={tableHandler.loading}
                       dataSource={groups?.data}
                       size="middle"
                       onChange={tableHandler.onTableChange}
                       pagination={tableHandler.pagination}
                       onRow={onRow}
                       rowKey="id"
                       columns={columns}
                />
            </>
        </DocumentTitle>
    );

    function renderName(value: any, group: SbomProjectGroup) {
        return <Link title={group.name} to={`/sbom-project-groups/${group.id}`}>{value}</Link>;
    }

    function renderOrganization(value: Organization) {
        return <Link to={`/organizations/${value.id}`}>{value.name}</Link>;
    }

    function renderProjects(_: any, group: SbomProjectGroup) {
        if (!group.projects?.length) {
            return <i>no projects</i>;
        }

        return (
            <div className={styles.projects}>
                <div className={styles["project-count"]}>
                    {group.projects.length} {group.projects.length === 1 ? "project" : "projects"}
                </div>

                <MoreLess textMore={"« show all projects »"} textLess={"» hide projects «"}>
                    <div className={styles["project-list"]}>
                        {group.projects.map(project =>
                            <Link
                                key={project.id}
                                className={styles["project-link"]}
                                title={project.name}
                                to={`/sbom-projects/${project.id}`}>
                                {project.name}
                            </Link>
                        )}
                    </div>
                </MoreLess>
            </div>
        );
    }

    function renderAction(_: any, group: SbomProjectGroup) {
        return (
            <Button icon={<DeleteFilled/>} className={"ant-btn-icon-only"}
                    title={"Delete"}
                    onClick={() => {
                        Modal.confirm({
                            content: <>Do you really want to delete project group <b>{group.name}</b>?</>,
                            okText: "Delete",
                            cancelText: "Cancel",
                            onOk: () => onDeleteConfirm(group)
                        });
                    }}/>
        );
    }

    function onDeleteConfirm(group: SbomProjectGroup) {
        sbomProjectGroupService.delete(group)
            .then(() => {
                message.success(<>Project group <b>{group.name}</b> successfully deleted.</>);

                reload();
            });
    }

    function reload() {
        return sbomProjectGroupService.getList(tableHandler.queryOptions).then(value => {
            tableHandler.updateTotal(value.total);

            setGroups(value);
        });
    }

    function onRow(group: SbomProjectGroup) {
        return {onDoubleClick: () => navigate(`/sbom-project-groups/${group.id}`)};
    }
}

export default SbomProjectGroupList;
